import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Stack, Typography, IconButton, Tooltip,
} from '@mui/material';
import { SmartToy, ExpandMore, Add, Check, Delete, Settings, WhatsApp } from '@mui/icons-material';
import { useAgentes, AgenteResumen } from '../context/AgenteContext';
import { api, CLAVE_AGENTE } from '../config/api';
import { leerError } from '../utils/errores';

const numeroDe = (a: AgenteResumen) => (a.numero ? `+${a.numero.replace(/^\+/, '')}` : 'Sin número conectado');

/**
 * Selector del agente con el que se trabaja en el panel (barra superior).
 * Cada agente tiene su número de WhatsApp, su prompt, su base de conocimiento,
 * sus conversaciones y su agenda.
 */
const SelectorAgente: React.FC = () => {
  const navigate = useNavigate();
  const { agentes, actual, limite, usados, enPrueba, cambiar, recargar } = useAgentes();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [admin, setAdmin] = useState(false);
  const [nombre, setNombre] = useState('');
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);

  if (!actual) return null;
  const puedeCrear = usados < limite;

  const crear = async () => {
    setError('');
    if (!nombre.trim()) return setError('Ponle un nombre al agente.');
    setOcupado(true);
    try {
      const { data } = await api.post('/agente/nuevo', { nombre: nombre.trim() });
      // Trabajar de inmediato con el agente nuevo, empezando por conectar su número.
      localStorage.setItem(CLAVE_AGENTE, String(data.agente.id));
      window.location.href = '/app/agente';
    } catch (err: any) {
      setError(leerError(err, 'No se pudo crear el agente.').message);
      setOcupado(false);
    }
  };

  const eliminar = async (a: AgenteResumen) => {
    if (!window.confirm(`¿Eliminar el agente "${a.nombre}"? Se desconecta su número y se borran sus conversaciones. Esto no se puede deshacer.`)) return;
    setOcupado(true);
    try {
      await api.delete(`/agente/${a.id}`);
      if (a.id === actual.id) {
        localStorage.removeItem(CLAVE_AGENTE);
        window.location.reload();
        return;
      }
      await recargar();
    } catch (err: any) {
      setError(leerError(err, 'No se pudo eliminar el agente.').message);
    } finally { setOcupado(false); }
  };

  return (
    <>
      <Button
        color="inherit"
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={<SmartToy color="primary" />}
        endIcon={<ExpandMore />}
        aria-label={`Agente actual: ${actual.nombre}. Cambiar de agente`}
        sx={{ textTransform: 'none', mr: 1, maxWidth: { xs: 170, sm: 280 }, border: '1px solid #E2E8F0', borderRadius: 2, px: 1.2 }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{actual.nombre}</Box>
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} PaperProps={{ sx: { minWidth: 280 } }}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">Agentes: {usados} de {limite}</Typography>
        </Box>
        {agentes.map((a) => (
          <MenuItem key={a.id} selected={a.id === actual.id} onClick={() => { setAnchor(null); cambiar(a.id); }}>
            <ListItemIcon>{a.id === actual.id ? <Check fontSize="small" /> : <SmartToy fontSize="small" />}</ListItemIcon>
            <ListItemText
              primary={<Stack direction="row" spacing={0.5} alignItems="center"><span>{a.nombre}</span>{!a.activo && <Chip size="small" label="Apagado" sx={{ height: 18, fontSize: 10 }} />}{!a.incluido_en_plan && <Chip size="small" color="warning" label="Fuera del plan" sx={{ height: 18, fontSize: 10 }} />}</Stack>}
              secondary={numeroDe(a)}
            />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => { setAnchor(null); setNombre(''); setError(''); setAdmin(true); }}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          <ListItemText primary="Administrar agentes" secondary={puedeCrear ? 'Crear o eliminar agentes' : undefined} />
        </MenuItem>
      </Menu>

      <Dialog open={admin} onClose={() => setAdmin(false)} fullWidth maxWidth="sm">
        <DialogTitle>Tus agentes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cada agente tiene su propio número de WhatsApp, sus instrucciones, su base de conocimiento, sus conversaciones y su agenda.
            El plan, el cupo de mensajes, los datos de tu empresa y las integraciones (Google, Mercado Libre, WooCommerce) se comparten.
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={1} sx={{ mb: 2 }}>
            {agentes.map((a) => (
              <Stack key={a.id} direction="row" alignItems="center" spacing={1.5} sx={{ p: 1.2, border: '1px solid #E2E8F0', borderRadius: 2 }}>
                <WhatsApp sx={{ color: a.conectado ? '#25D366' : 'text.disabled' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle2" noWrap>{a.nombre}</Typography>
                    {a.es_principal && <Chip size="small" label="Principal" sx={{ height: 20 }} />}
                    {!a.activo && <Chip size="small" label="Apagado" sx={{ height: 20 }} />}
                    {!a.incluido_en_plan && <Chip size="small" color="warning" label="Fuera del plan" sx={{ height: 20 }} />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">{numeroDe(a)}{a.numero ? ` · ${a.modo === 'qr' ? 'QR' : 'API oficial'}` : ''}</Typography>
                </Box>
                {!a.numero && (
                  <Button size="small" onClick={() => { setAdmin(false); if (a.id !== actual.id) { localStorage.setItem(CLAVE_AGENTE, String(a.id)); window.location.href = '/app/conexion'; } else navigate('/app/conexion'); }}>
                    Conectar número
                  </Button>
                )}
                {!a.es_principal && (
                  <Tooltip title="Eliminar agente">
                    <span><IconButton aria-label={`Eliminar ${a.nombre}`} color="error" onClick={() => eliminar(a)} disabled={ocupado}><Delete /></IconButton></span>
                  </Tooltip>
                )}
              </Stack>
            ))}
          </Stack>

          {puedeCrear ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField size="small" fullWidth label="Nombre del nuevo agente" placeholder="Ej: Ventas, Soporte, Sucursal Providencia"
                value={nombre} onChange={(e) => setNombre(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && crear()} inputProps={{ maxLength: 160 }} />
              <Button variant="contained" startIcon={<Add />} onClick={crear} disabled={ocupado} sx={{ whiteSpace: 'nowrap' }}>Crear agente</Button>
            </Stack>
          ) : (
            <Alert severity="info" action={enPrueba ? <Button color="inherit" size="small" onClick={() => { setAdmin(false); navigate('/app/suscripcion'); }}>Ver plan</Button> : undefined}>
              {enPrueba
                ? 'En la prueba gratis puedes tener 1 agente. Con el plan Pro tienes hasta 3, cada uno con su número de WhatsApp.'
                : `Tu plan permite ${limite} agente${limite === 1 ? '' : 's'} y ya los estás usando todos.`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAdmin(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SelectorAgente;
